import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, ArrowLeft, Sparkles, RefreshCw, Calendar } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const AffirmationsPage = () => {
  const navigate = useNavigate();
  const [affirmations, setAffirmations] = useState([]);
  const [dailyAffirmation, setDailyAffirmation] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewIndex, setViewIndex] = useState(0);

  useEffect(() => {
    fetchAffirmations();
  }, []);

  const fetchAffirmations = async () => {
    try {
      const [affRes, dailyRes] = await Promise.all([
        axios.get(`${API}/content/affirmations`),
        axios.get(`${API}/content/daily`)
      ]);
      setAffirmations(affRes.data.affirmations);
      setTotalCount(affRes.data.total_count);
      setDailyAffirmation(dailyRes.data.affirmation);
    } catch (error) {
      console.error("Error fetching affirmations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRandomAffirmation = () => {
    const newIndex = Math.floor(Math.random() * affirmations.length);
    setViewIndex(newIndex);
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="affirmations-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="affirmations-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Affirmations</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-3">
            365 Days of Affirmations
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            A unique affirmation for every day of the year—{totalCount} words to nurture your soul
          </p>
        </div>

        {/* Today's Affirmation - Featured */}
        <Card className="glass-card rounded-3xl border-0 mb-10 overflow-hidden">
          <CardContent className="p-8 md:p-12 text-center relative">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Calendar className="w-5 h-5 text-primary" />
              <span className="text-sm text-primary">Day {dailyAffirmation?.day} of 365</span>
            </div>
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-8 h-8 text-primary" strokeWidth={1.5} />
            </div>
            <p className="font-heading text-2xl md:text-3xl text-foreground italic leading-relaxed" data-testid="featured-affirmation">
              "{dailyAffirmation?.text}"
            </p>
            <p className="text-sm text-muted-foreground mt-6">Today's Affirmation</p>
          </CardContent>
        </Card>

        {/* Random Affirmation Button */}
        <div className="text-center mb-10">
          <Button
            onClick={handleRandomAffirmation}
            className="btn-secondary"
            data-testid="random-affirmation-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Show Me Another
          </Button>
        </div>

        {/* Sample Affirmations Grid */}
        <div className="mb-8">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-4">
            Sample from the Collection
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {affirmations.slice(viewIndex, viewIndex + 10).map((affirmation, index) => (
              <Card
                key={affirmation.id}
                className="glass-card rounded-2xl border-0 card-hover"
                data-testid={`affirmation-${affirmation.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="font-heading text-primary font-semibold text-sm">
                        {affirmation.day}
                      </span>
                    </div>
                    <p className="text-foreground leading-relaxed">
                      {affirmation.text}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Closing Message */}
        <div className="text-center py-8">
          <p className="font-handwritten text-2xl text-primary">
            "You are worthy of every kind word you speak to yourself"
          </p>
        </div>
      </main>
    </div>
  );
};

export default AffirmationsPage;
